"use client";

// "Add Connector" type-selection modal — Figma: OTEN-AIS_In-review nodes
// 7952-138394 (over listing) / 7995-127831 (MCP card hover state, container
// 7995-130111). Jira AIS-1835 / AI-STORY-054 (PRD flow 5): choose between a
// System connector and an MCP connection before the matching setup flow opens.
//
// Behavior (per Harrison, 2026-06-12): the MCP card hands off to the Add MCP
// connection modal (our flow); the System connector card is intentionally
// inert this batch — its flow is untouched and will be wired later. × / ESC /
// backdrop close without creating anything (AC1).

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { Blend, Layers2, X } from "lucide-react";
import { usePageScrollLock } from "@/hooks/use-page-scroll-lock";
import { domId } from "@/lib/dom-id";

export interface ChooseConnectionTypeModalProps {
  open: boolean;
  onClose: () => void;
  /** MCP connection card → open the Add MCP connection modal. */
  onSelectMcp: () => void;
  /** System connector card — optional; absent = inert (current batch). */
  onSelectSystem?: () => void;
  allowSystem?: boolean;
  allowMcp?: boolean;
}

export default function ChooseConnectionTypeModal({
  open,
  onClose,
  onSelectMcp,
  onSelectSystem,
  allowSystem = true,
  allowMcp = true,
}: ChooseConnectionTypeModalProps) {
  const t = useTranslations("AddConnectorModal");
  const purpose = "choose-connection-type";
  // The backdrop is a plain overlay, so the page behind it would keep
  // scrolling under the wheel; lock it like the other custom overlays do.
  usePageScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid={domId("mdl", purpose)}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-lg bg-white p-4 shadow-[0px_3px_12px_rgba(0,0,0,0.13)]">
        {/* Header — Bold 20 title + × ; 10px gray subtitle */}
        <div className="flex flex-col">
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-bold leading-[1.4] text-[#444746]">
              {t("title")}
            </h2>
            <button
              type="button"
              aria-label={t("close")}
              data-testid={domId("btn", `${purpose}-close`)}
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center rounded-lg text-[#6D7E91] transition hover:bg-gray-100"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <p className="text-[10px] leading-[1.4] text-[#4C6075]">
            {t("subtitle")}
          </p>
        </div>

        {/* System connector — intentionally inert until its flow is wired. */}
        {allowSystem && (
          <button
            type="button"
            data-testid={domId("btn", `${purpose}-system`)}
            onClick={() => onSelectSystem?.()}
            className="flex h-20 w-full items-start gap-1 rounded-lg border border-[#DCDFE3] bg-white p-2 text-left transition-colors hover:bg-[#EEEEFB]"
          >
            <span className="flex shrink-0 items-center rounded bg-[#D7D6F5] p-1">
              <Blend size={16} className="text-[#4B49B6]" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col justify-center leading-[1.4]">
              <span className="text-sm font-medium text-[#4B49B6]">
                {t("system.title")}
              </span>
              <span className="text-[10px] font-normal text-[#4C6075]">
                {t("system.desc")}
              </span>
              <span className="text-[10px] font-semibold text-[#B2BBC4]">
                {t("system.bestFor")}
              </span>
            </span>
          </button>
        )}

        {/* MCP connection — opens the Add MCP connection modal (our flow). */}
        {allowMcp && (
          <button
            type="button"
            data-testid={domId("btn", `${purpose}-mcp`)}
            onClick={onSelectMcp}
            className="flex h-20 w-full items-start gap-1 rounded-lg border border-[#DCDFE3] bg-white p-2 text-left transition-colors hover:bg-[#EEEEFB]"
          >
            <span className="flex shrink-0 items-center rounded bg-[#CDECF2] p-1">
              <Layers2 size={16} className="text-[#10A5A5]" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col justify-center leading-[1.4]">
              <span className="text-sm font-medium text-[#10A5A5]">
                {t("mcp.title")}
              </span>
              <span className="text-[10px] font-normal text-[#4C6075]">
                {t("mcp.desc")}
              </span>
              <span className="text-[10px] font-semibold text-[#B2BBC4]">
                {t("mcp.bestFor")}
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
